import { z } from 'zod';
import { requirementKeySchema } from './requirements.js';
import { triggerSchema } from './triggers.js';
/**
 * Roles are data (decision 3), and so are tags.
 *
 * NAMING: `role` already means Entra app role in this codebase (`apps/api/src/functions/
 * roles.ts`, HR_Admin / HR_Viewer). HR roles are JobProfiles in code, ALWAYS — two
 * meanings of one word eventually produces a wrong authorization check.
 *
 * The compliance catalog is already written as composition — "Sets B + C + D, plus the
 * licensure rows" — so the schema is composition too, rather than 17 flat lists that must
 * be edited 17 times when one rule changes.
 */
export const profileKeySchema = z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/);
export const personTagSchema = z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/);
/**
 * A binding carries ORDER and a note, and nothing else.
 *
 * Profiles deliberately cannot override policy. An override surface of 140 rules x 17
 * profiles is a second configuration system competing with the first, and the question
 * "is CPR blocking?" would then have seventeen answers in seventeen documents — exactly
 * the piecemeal edit the Phase 1 constraint forbids.
 *
 * Role-varying behaviour has two legitimate expressions already: a trigger clause on the
 * person's profiles, or a distinct requirement key.
 */
export const requirementBindingSchema = z.object({
    requirementKey: requirementKeySchema,
    /**
     * Sort position, and it is GLOBAL rather than set-local. Two sets composed onto one
     * person interleave strictly by this number, so seed data must assign disjoint bands
     * (core 0-99, clinical 100-199, driving 200-299) or an unrelated set will slot rows
     * into the middle of another's run. The resolver deliberately does not offset members
     * by their set's position in `includes` — implicit renumbering would make a row's
     * position depend on composition order, which is exactly what is hard to predict when
     * a profile is edited.
     */
    order: z.number().int().nonnegative(),
    note: z.string().default(''),
});
/** Set B, Set C, Set D from the catalog: named, reusable, edited once. */
export const requirementSetSchema = z.object({
    id: z.string().min(1),
    docType: z.literal('requirement-set'),
    key: profileKeySchema,
    label: z.string().min(1),
    members: z.array(requirementBindingSchema).default([]),
    retiredAt: z.string().datetime().nullable().default(null),
});
/**
 * ONE composition grammar, used VERBATIM by both job profiles and tag profiles. That
 * shared grammar is what keeps roles and tags from becoming two mechanisms: they differ
 * only in what selects them, never in what they can express.
 */
export const requirementCompositionSchema = z.object({
    includes: z.array(profileKeySchema).default([]),
    adds: z.array(requirementBindingSchema).default([]),
    /**
     * Resolves the catalog's "pick one" conflicts. Seeding both sides of a pick-one pair
     * produces permanently contradictory due dates on one employee.
     */
    removes: z.array(requirementKeySchema).default([]),
});
export const jobProfileSchema = z.object({
    id: z.string().min(1),
    docType: z.literal('job-profile'),
    key: profileKeySchema,
    /** Configurable, exactly like a requirement label. */
    label: z.string().min(1),
    description: z.string().default(''),
    /**
     * Decision 4 without a hard-coded key: the CPR set is attached to profiles flagged
     * direct-care, so "who needs CPR" is answered by set membership rather than by an
     * `if` on a requirement key somewhere in the app.
     */
    directCare: z.boolean().default(false),
    composition: requirementCompositionSchema,
    displayOrder: z.number().int().nonnegative().default(0),
    retiredAt: z.string().datetime().nullable().default(null),
});
/**
 * Contractor and Driver are tags, not profiles (decision 3). A tag profile is the same
 * composition selected by a tag instead of a profile, so the driving set attaches to
 * anyone tagged `driver` and to nobody else — no duplicated people, no "RN (driving)".
 */
export const tagProfileSchema = z.object({
    id: z.string().min(1),
    docType: z.literal('tag-profile'),
    tag: personTagSchema,
    label: z.string().min(1),
    composition: requirementCompositionSchema,
    /** A tag may itself be conditional — e.g. only meaningful while employed. */
    activeWhen: triggerSchema.nullable().default(null),
    retiredAt: z.string().datetime().nullable().default(null),
});
//# sourceMappingURL=profiles.js.map